import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import jwt_decode from "jwt-decode";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { WEB_SERVICE_MIMS, project, applications } from "../../config/constants";
import { isAISNProjectHospital } from "../../config/projectFunctions";
// Components
import {
  SelectAligner,
  SelectDiv,
  InputDataDiv,
  Input,
  SearchIcon,
  RemoveContentButton,
  RemoveContentIcon,
  TableHeader,
  HeaderSection,
  HeaderButton,
  SetBtnImg,
  HeaderText,
  NoPatientsText,
} from "./styles";
import UseFetch from "../../config/useFetch";
import Loader from "../loader";
import Title from "../title";
import PatientData from "../patientData";
import { adherenceFilter, userFilter, applicationFilter } from "./sections/patientFilters";
import Select from "../selectInput";
// Assets
import IcoArrowUp from "../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../assets/icons/ico_arrow_down.svg";
import IcoMagnifyingGlass from "../../assets/icons/magnifying_glass.svg";
import IcoRemove from "../../assets/icons/btn_close_red.svg";

// Must match PATIENTS_LIMIT of the list-in-blocks endpoint
const PATIENTS_BLOCK_SIZE = 50;

const Patients = () => {
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [hospitalApplications, setHospitalApplications] = useState([]);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientsByClinician, setPatientsByClinician] = useState([]);
  const [filterByHospitalOrClinician, setFilterByHospitalOrClinician] = useState("HOSPITAL");
  const [targetedPatients, setTargetedPatients] = useState([]);
  const [currentFilter, setCurrentFilter] = useState("ALPHABET");
  const [isInitialFilter, setIsInitialFilter] = useState(true);
  const [filteredSoftwareId, setFilteredSoftwareId] = useState(0);
  const [filteredPatient, setFilteredPatient] = useState("");
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [isFetchingMorePatients, setIsFetchingMorePatients] = useState(true);

  const tokenFromStorage = useSelector(({ clinician }) => clinician?.data?.token);
  const { clinicianToken } = useParams();
  const clinicianData = jwt_decode(clinicianToken);
  const hospitalId = clinicianData.data.HOSPITAL_ID;
  const hospitalName = clinicianData.data.HOSPITAL_NAME;
  const patientId = clinicianData.data.PATIENT_ID;
  const clinicianId = clinicianData.data.CLINICIAN_ID;
  const clinicianRole = clinicianData.data.ROLE;
  const clinicianUser = clinicianData.data.CLINICIAN_USER;

  const [t] = useTranslation("global");

  // Fetch hospital applications
  useEffect(() => {
    if (tokenFromStorage) {
      UseFetch("get", `${WEB_SERVICE_MIMS}/hospitals/software/${hospitalId}`)
        .then(({ data }) => {
          setHospitalApplications(
            data.filter(
              (application) => application.SOFTWARE_ID !== applications.home.id && application.SOFTWARE_ID !== applications.web.id,
            ),
          );
        })
        .catch((error) => {
          console.log("Get hospital software: ", error);
        });
    }

    return () => {
      setHospitalApplications([]);
    };
  }, [hospitalId, clinicianToken, hospitalName, tokenFromStorage]);

  // Fetch patients
  useEffect(() => {
    const getPatientsSignal = new AbortController();
    let fetchMorePatients = true;

    const fetchPatients = async () => {
      let offset = 0;

      while (fetchMorePatients) {
        const patientsListEnvironment = {
          global: `${WEB_SERVICE_MIMS}/patients/list-in-blocks/${hospitalId}/${clinicianUser}/${clinicianRole}/${offset}`,
          strack: `${WEB_SERVICE_MIMS}/patients/list/${hospitalId}/${patientId}/${clinicianRole}`,
        };
        const patientListURL = patientsListEnvironment[project];

        try {
          const { data } = await UseFetch("get", patientListURL, getPatientsSignal);
          if (data.length) {
            setPatients((prevPatients) => [...prevPatients, ...data]);
            offset += PATIENTS_BLOCK_SIZE;
          }
          // A partial block is the last one, so there is no need to ask for an empty one
          if (project !== "global" || data.length < PATIENTS_BLOCK_SIZE) {
            fetchMorePatients = false;
            setIsFetchingMorePatients(false);
          }
          setIsLoadingPatients(false);
        } catch (error) {
          if (getPatientsSignal.signal.aborted) return;
          console.log("Get patients: ", error);
          fetchMorePatients = false;
          setIsFetchingMorePatients(false);
          setIsLoadingPatients(false);
        }
      }
    };
    if (tokenFromStorage) fetchPatients();

    return () => {
      fetchMorePatients = false;
      setPatients([]);
      setTargetedPatients([]);
      setIsLoadingPatients(true);
      if (getPatientsSignal) {
        getPatientsSignal.abort();
      }
    };
  }, [hospitalId, clinicianUser, patientId, clinicianRole, tokenFromStorage]);

  // Set patients by clinician
  useEffect(() => {
    const filteringPatientsByClinician = patients.filter((patient) => patient.patientClinicianId === clinicianId);
    setPatientsByClinician(filteringPatientsByClinician);
  }, [patients, clinicianId]);

  // Set targeted patients
  useEffect(() => {
    setTargetedPatients(filterByHospitalOrClinician === "HOSPITAL" ? patients : patientsByClinician);
  }, [filterByHospitalOrClinician, patients, patientsByClinician]);

  // Set filtered patient
  useEffect(() => {
    const norm = (s) =>
      s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    const normFilteredUser = norm(filteredPatient);
    const filteredPatients = filteredPatient ? patients.filter((patient) => norm(patient.PATIENT_USER).includes(normFilteredUser)) : [];
    setFilteredPatients(filteredPatients);
  }, [filteredPatient, patients]);

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };

    if (tokenFromStorage) handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName, tokenFromStorage]);

  const renderPatients = () => {
    const patientsList = filteredPatient ? filteredPatients : targetedPatients;
    return patientsList.map((patient, i) => {
      const patientId = patient.PATIENT_ID;
      return (
        <PatientData
          key={patientId}
          patientIndex={i}
          patientId={patientId}
          isThereSomePrescription={patient.isThereSomePrescription}
          isTherePrescriptionsAfter7Days={patient.isTherePrescriptionsAfter7Days}
          isThePrescriptionMissing={patient.isThePrescriptionMissing}
          adherence={patient.ADHERENCE}
          user={patient.PATIENT_USER}
          gender={patient.GENDER}
          birthDate={patient.BIRTH_DATE}
          pathology={patient.PATHOLOGY}
          patientApplications={patient.SOFTWARE_ID}
          hospitalApplications={hospitalApplications}
          filteredSoftwareId={filteredSoftwareId}
          currentFilter={currentFilter}
          isAISNHospital={isAISNHospital}
        />
      );
    });
  };

  const renderFilterPatientsSelect = () => {
    return (
      <SelectAligner>
        <SelectDiv>
          <Select
            value={filterByHospitalOrClinician}
            onChange={(e) => setFilterByHospitalOrClinician(e.target.value)}
            padding="5px 32px 5px 10px"
            width="200px"
            height="32px"
            arrowImg={IcoArrowDown}
            backgroundPosition="175px center"
          >
            <option value="HOSPITAL">{t("all")}</option>
            <option value="CLINICIAN">{t("myPatients")}</option>
          </Select>
        </SelectDiv>
        <InputDataDiv>
          <SearchIcon src={IcoMagnifyingGlass} alt="search-ico" />
          <Input
            type="text"
            placeholder={t("home_searchPatients")}
            id="searchUser"
            value={filteredPatient}
            onChange={(e) => setFilteredPatient(e.target.value)}
          />
          {filteredPatient && (
            <RemoveContentButton onClick={() => setFilteredPatient("")}>
              <RemoveContentIcon src={IcoRemove} alt="remove-ico" />
            </RemoveContentButton>
          )}
        </InputDataDiv>
      </SelectAligner>
    );
  };

  const handleSetAdherenceFilter = () => {
    adherenceFilter(targetedPatients, currentFilter, setCurrentFilter, isInitialFilter, setIsInitialFilter, setTargetedPatients);
  };

  const handleSetUserFilter = () => {
    userFilter(targetedPatients, currentFilter, setCurrentFilter, isInitialFilter, setIsInitialFilter, setTargetedPatients);
  };

  const handleSetApplicationFilter = () => {
    applicationFilter(
      targetedPatients,
      currentFilter,
      setCurrentFilter,
      hospitalApplications,
      filteredSoftwareId,
      setFilteredSoftwareId,
      setTargetedPatients,
    );
  };

  const renderHeader = () => {
    return (
      <TableHeader>
        <HeaderSection width="70px">
          <HeaderButton onClick={handleSetAdherenceFilter}>
            {t("home_adherenceFilter")}
            {currentFilter === "ADHERENCE" && <SetBtnImg src={isInitialFilter ? IcoArrowDown : IcoArrowUp} alt="ico-arrow" />}
          </HeaderButton>
        </HeaderSection>
        <HeaderSection width="220px">
          <HeaderButton onClick={handleSetUserFilter}>
            {t("user").toUpperCase()}
            {currentFilter === "ALPHABET" && <SetBtnImg src={isInitialFilter ? IcoArrowDown : IcoArrowUp} alt="ico-arrow" />}
          </HeaderButton>
        </HeaderSection>
        <HeaderSection width="90px">
          <HeaderText>{t("profile").toUpperCase()}</HeaderText>
        </HeaderSection>
        <HeaderSection width="280px">
          <HeaderText>{t("information").toUpperCase()}</HeaderText>
        </HeaderSection>
        <HeaderSection width="200px" justifyContent="flex-end">
          <HeaderButton onClick={handleSetApplicationFilter}>{t("applications").toUpperCase()}</HeaderButton>
        </HeaderSection>
      </TableHeader>
    );
  };

  return (
    <>
      <Title title={t("patients").toUpperCase()} marginBottom="15px" />
      {renderFilterPatientsSelect()}
      {isLoadingPatients ? (
        <Loader />
      ) : (
        <>
          {Boolean(targetedPatients.length) ? (
            filteredPatient && !filteredPatients.length && isFetchingMorePatients ? (
              <Loader />
            ) : filteredPatient && !filteredPatients.length ? (
              <NoPatientsText>{t("home_noUserPatients")}</NoPatientsText>
            ) : (
              <>
                {renderHeader()}
                {renderPatients()}
              </>
            )
          ) : (
            <NoPatientsText>{t("home_registeredPatients")}</NoPatientsText>
          )}
        </>
      )}
    </>
  );
};

export default Patients;
